import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { SearchBar } from './SearchBar';
import { cn } from '@/lib/utils';
import { useScrollHeader } from '@/hooks/useScrollHeader';

export const Header = () => {
  const { t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const scrolled = useScrollHeader(180);

  const navLinks = [
    { href: '/category/mobile-living', label: t('nav.mobileLiving') },
    { href: '/category/off-grid-homes', label: t('nav.offGridHomes') },
    { href: '/category/boat-living', label: t('nav.boatLiving') },
    { href: '/category/camping', label: t('nav.camping') },
    { href: '/glossary', label: t('nav.glossary') },
    { href: '/tools', label: t('nav.tools') },
  ];

  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsMenuOpen(false);
      setIsSearchOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  return (
    <header
      className={cn(
        'sticky top-0 z-50 bg-background/95 backdrop-blur-md transition-all duration-500 ease-editorial',
        scrolled ? 'border-b border-brass/20 shadow-sm' : 'border-b border-border/30',
      )}
    >
      <div className="container mx-auto px-6 md:px-8">
        <div
          className={cn(
            'flex items-center justify-between transition-[height] duration-500 ease-editorial',
            scrolled ? 'h-14 md:h-16' : 'h-16 md:h-20',
          )}
        >
          {/* Logo */}
          <Link
            to="/"
            className={cn(
              'font-display font-medium text-primary hover:text-primary/80 transition-all duration-500 tracking-wide',
              scrolled ? 'text-lg md:text-xl' : 'text-xl md:text-2xl',
            )}
          >
            Modern Nomad Living
          </Link>

          {/* Desktop Navigation — centered */}
          <nav
            className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2"
            aria-label="Main navigation"
          >
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="font-mono text-[10px] font-normal tracking-editorial uppercase text-foreground/70 hover:text-brass transition-colors duration-500"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-3 md:gap-5">
            {/* Search toggle */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 text-foreground/50 hover:text-brass transition-colors duration-500"
              aria-label={t('nav.search')}
              aria-expanded={isSearchOpen}
              aria-controls="search-bar"
            >
              <Search className="w-4 h-4" strokeWidth={1.5} />
            </button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-foreground/50 hover:text-brass transition-colors duration-500"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-nav"
            >
              {isMenuOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div
          id="search-bar"
          className={cn(
            'overflow-hidden transition-all duration-500 ease-in-out',
            isSearchOpen ? 'max-h-20 pb-5' : 'max-h-0'
          )}
        >
          {isSearchOpen && (
            <SearchBar onClose={() => setIsSearchOpen(false)} autoFocus />
          )}
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        id="mobile-nav"
        className={cn(
          'lg:hidden overflow-hidden transition-all duration-500 ease-in-out bg-background border-b border-brass/15',
          isMenuOpen ? 'max-h-72' : 'max-h-0'
        )}
      >
        <nav className="container mx-auto px-6 py-6 flex flex-col gap-5" aria-label="Mobile navigation">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setIsMenuOpen(false)}
              className="font-mono text-[11px] font-normal tracking-editorial uppercase text-foreground/70 hover:text-brass transition-colors duration-500 py-1"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
};
